import React, { ReactElement } from 'react';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import classnames from 'classnames/bind';
import _ from 'lodash';

import { DAILY_BUDGET_STATE } from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignBasicFormModel';
import {
  DailyBudgetPlan,
  DeliverType,
  RtbCampaignPlanType,
  VideoAdMetricEvent,
  VideoAdViewObjective
} from 'core/rtbCampaign/RtbCampaign';
import styles from './rtbCampaignInfo.module.scss';
import i18n from 'i18n';
import { AlertDialog } from 'components/AlertDialog';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { addOnEnabled, notRoles } from 'core/permission/PermissionDSL';
import { RoleNames } from 'core';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { Trans } from 'react-i18next';
import { FormikField } from 'components/common/form/field/FormikField';
import { Link } from 'react-router-dom';
import { LabelField } from 'components/common/form/field/LabelField';
import { CustomField } from 'components/common/form/field/CustomField';
import { RadioField } from 'components/common/form/field/RadioField';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { getCurrencyLabel } from 'helper/CurrencyHelper';
import { getDisplayPrice } from 'helper/CampaignPriceHelper';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import Tips from 'components/common/Tips/Tips';

const cx = classnames.bind(styles);

export type CampaignInfoState = {
  alertContent?: string | ReactElement | ReactElement[];
  needShowDailyBudgetAlert: boolean;
};

const videoAdMetricEventOptions: SelectOptions[] = createSelectOptionsFromEnum(
  VideoAdMetricEvent,
  'campaign.labels.'
);

class RtbCampaignInfo extends React.Component<any, CampaignInfoState> {
  turnOnNeedShowDailyBudgetAlert: () => void;

  dudgetTypeOptions = [
    {
      label: i18n.t('campaignInfo.labels.schedule'),
      value: DailyBudgetPlan.SCHEDULE,
      extra: i18n.t('campaignInfo.labels.cannotModifyHint')
    },
    {
      label: i18n.t('campaignInfo.labels.daily'),
      value: DailyBudgetPlan.DAILY,
      extra: i18n.t('campaignInfo.labels.cannotModifyHint')
    }
  ];

  constructor(props) {
    super(props);
    this.state = {
      alertContent: undefined,
      needShowDailyBudgetAlert: false
    };
    this.turnOnNeedShowDailyBudgetAlert = _.partial(
      this.setNeedShowDailyBudgetAlert,
      true
    );
  }

  get currencyLabel() {
    return getCurrencyLabel(this.props.currency);
  }

  getDailyBudgetTips() {
    const { dailyBudgetState } = this.props;
    switch (dailyBudgetState) {
      case DAILY_BUDGET_STATE.OVER_BUDGET:
        return (
          <Trans
            i18nKey='campaign.descriptions.overDailyBudget'
            components={{ 1: <span className='text-danger' /> }}
          />
        );
      case DAILY_BUDGET_STATE.UNDER_BUDGET:
        return (
          <Trans
            i18nKey='campaign.descriptions.underDailyBudget'
            components={{ 1: <span className='text-danger' /> }}
          />
        );
      case DAILY_BUDGET_STATE.MEET_BUDGET:
        return (
          <Trans
            i18nKey='campaign.descriptions.meetDailyBudget'
            components={{ 1: <span className='text-dark' /> }}
          />
        );
      case DAILY_BUDGET_STATE.DEFAULT:
      default:
        return;
    }
  }

  setNeedShowDailyBudgetAlert = (needShow) => {
    this.setState({
      needShowDailyBudgetAlert: needShow
    });
  };

  checkIsUnderBudget = () => {
    if (
      this.state.needShowDailyBudgetAlert &&
      this.props.dailyBudgetState === DAILY_BUDGET_STATE.UNDER_BUDGET
    ) {
      this.setState({
        alertContent: (
          <Trans
            i18nKey='campaign.descriptions.underDailyBudget'
            components={{ 1: <span className='text-danger' /> }}
          />
        )
      });
    }
  };

  dismissAlertWindow = () => {
    this.setState({
      alertContent: undefined,
      needShowDailyBudgetAlert: false
    });
  };

  renderTargetBudgetInput = () => {
    const {
      priceModel,
      budget,
      orderPrice,
      adminBidPrice,
      showBudgetHint,
      remainBudget
    } = this.props;
    let value = 0;
    // RKB-2716: 目標數要跟著畫面上顯示的價格算，否則會出現「合約價 266 / 目標 424」
    // 這種對不起來的組合（424 是用原價 280 算的）。
    const displayPrice = getDisplayPrice(adminBidPrice, orderPrice);
    const orderPriceNumber =
      typeof displayPrice === 'string'
        ? parseFloat(displayPrice)
        : displayPrice;
    if (orderPriceNumber && orderPriceNumber >= 0) {
      value = Math.round(budget / orderPriceNumber);
    }

    let postText;
    switch (priceModel) {
      case RtbCampaignPlanType.FCPC:
        postText = i18n.t('campaign.labels.targetClicks', { value });
        break;
      case RtbCampaignPlanType.FCPM:
      case RtbCampaignPlanType.FWCPM:
        postText = i18n.t('campaign.labels.targetKImpressions', {
          value
        });
        break;
      case RtbCampaignPlanType.FCPV:
        postText = i18n.t('campaign.labels.targetViews', { value });
        break;
      case RtbCampaignPlanType.FACPM:
        postText = i18n.t('campaign.labels.targetKPublisherImpressions', {
          value
        });
        break;
      case RtbCampaignPlanType.FBCPM:
        postText = i18n.t('campaign.labels.targetMessages', {
          value
        });
        break;
      default:
        postText = '';
    }

    let hint: ReactElement | undefined;
    if (showBudgetHint) {
      hint = (
        <Trans
          i18nKey='campaign.descriptions.remainBudget'
          values={{ currency: this.currencyLabel, remainBudget }}
          components={{ 1: <span className='text-dark' /> }}
        />
      );
    }

    const inputProps = {
      label: i18n.t('campaignInfo.labels.totalBudget'),
      prefix: this.currencyLabel,
      name: 'budget',
      type: 'number',
      min: 0,
      autoComplete: 'off',
      onBlur: this.checkIsUnderBudget,
      onChange: this.turnOnNeedShowDailyBudgetAlert,
      hint,
      fieldContentWidth: 184
    };

    return postText ? (
      <FormikField.InputGroup {...inputProps} postText={postText} />
    ) : (
      <FormikField.InputGroup {...inputProps} />
    );
  };

  renderReadOnlyViewObjective() {
    if (!this.props.videoAdViewObjective) {
      return <div />;
    }

    const { offset, videoAdEvent, videoAdMetricEvent } =
      this.props.videoAdViewObjective;
    const isVideoProgress = videoAdEvent === VideoAdViewObjective.PROGRESS;
    const isVideoMetric = videoAdEvent === VideoAdViewObjective.METRIC;
    let contentLabel = i18n.t('campaign.labels.videoObjectiveDefault');
    if (isVideoProgress) {
      contentLabel = `${offset} ${i18n.t('campaign.labels.sec')}`;
    }
    if (isVideoMetric) {
      contentLabel = i18n.t(
        `campaign.labels.${videoAdMetricEvent.toLowerCase()}`
      );
    }
    return (
      <LabelField
        name='videoAdEvent'
        label={i18n.t('campaign.labels.videoObjective')}
        value={contentLabel}
      />
    );
  }

  renderViewObjective() {
    const {
      canEditPriceModel,
      videoAdViewObjective,
      videoAdViewObjectiveOptions
    } = this.props;

    if (!canEditPriceModel) {
      return this.renderReadOnlyViewObjective();
    }
    const isVideoProgress = videoAdViewObjective
      ? videoAdViewObjective.videoAdEvent === VideoAdViewObjective.PROGRESS
      : false;
    const isVideoMetric = videoAdViewObjective
      ? videoAdViewObjective.videoAdEvent === VideoAdViewObjective.METRIC
      : false;
    return (
      <>
        <FormikField.Radio
          label={i18n.t('campaign.labels.videoObjective')}
          className={styles.radio}
          options={videoAdViewObjectiveOptions}
          name='videoAdViewObjective.videoAdEvent'
        />
        {isVideoProgress && (
          <FormikField.InputGroup
            label=''
            className={styles.videoAdviewObjectiveAppend}
            prefix={i18n.t('campaign.prefixs.videoObjectiveProgress')}
            name='videoAdViewObjective.offset'
            type='number'
          />
        )}
        {isVideoMetric && (
          <FormikField.InputGroup
            label=''
            prefix={i18n.t('campaign.prefixs.videoObjectiveMetric')}
            name='videoAdViewObjective.videoAdMetricEvent'
          >
            <FormikField.Select
              name='videoAdViewObjective.videoAdMetricEvent'
              options={videoAdMetricEventOptions}
              className={styles.videoAdMetricEvent}
              simpleValue
              fieldContentWidth='auto'
              formGroupClassName={styles.marginBottom0}
            />
          </FormikField.InputGroup>
        )}
      </>
    );
  }

  renderOrderPrice(priceModel, canEditPriceModel) {
    const {
      orderPriceMinimum,
      showOrderPriceHint,
      onChangeOrderPrice,
      orderPrice,
      adminBidPrice
    } = this.props;

    switch (priceModel) {
      case RtbCampaignPlanType.FCPC:
      case RtbCampaignPlanType.FCPM:
      case RtbCampaignPlanType.FCPV:
      case RtbCampaignPlanType.FVCPM:
      case RtbCampaignPlanType.FACPM:
      case RtbCampaignPlanType.FWCPM:
      case RtbCampaignPlanType.FBCPM: {
        const price = `${this.currencyLabel}${orderPriceMinimum}`;
        const tips = showOrderPriceHint ? (
          <Trans
            i18nKey='campaign.descriptions.orderPriceTips'
            values={{ price }}
            components={{ 1: <span className='text-dark' /> }}
          />
        ) : (
          ''
        );
        const label =
          i18n.t(`campaign.labels.${priceModel}`) +
          i18n.t('campaign.labels.orderPrice');
        return canEditPriceModel ? (
          <FormikField.InputGroup
            label={label}
            name='orderPrice'
            prefix={this.currencyLabel}
            hint={tips}
            type='number'
            fieldContentWidth={184}
            onChange={onChangeOrderPrice}
          />
        ) : (
          <FormikField.InputGroup
            as='label'
            label={label}
            name='orderPrice'
            prefix={this.currencyLabel}
            prefixBorder={false}
            type='number'
            fieldContentWidth={'auto'}
            className={styles.priceLabel}
            postfix={
              adminBidPrice ? (
                <IconWithTooltip
                  icon={faExclamationCircle}
                  tooltipProps={{
                    id: `adminBidPriceTip`,
                    tooltip: i18n.t('campaign.labels.orderPriceChange', {
                      from: `${this.currencyLabel}${orderPrice}`,
                      to: `${this.currencyLabel}${adminBidPrice}`
                    })
                  }}
                />
              ) : undefined
            }
            postfixBorder={false}
          >
            {/*
              RKB-2716: 顯示 admin 調整過的價格。這裡用 children 而不是換掉欄位的值，
              因為這個欄位的值會被整包送進儲存 payload（見 RtbCampaignManager.wrapCampaignForServer），
              動它使用者一按儲存就會把合約價真的改掉。children 只影響畫面。
            */}
            <Form.Label className='col-form-label form-label'>
              {getDisplayPrice(adminBidPrice, orderPrice)}
            </Form.Label>
          </FormikField.InputGroup>
        );
      }
      case RtbCampaignPlanType.RS:
      case RtbCampaignPlanType.DCPM:
      default:
        return;
    }
  }

  renderDailyBudgetPlan() {
    const { dailyBudget } = this.props;
    const isScheduleDistribution = _.isNil(dailyBudget) || dailyBudget === 0;
    return (
      <CustomField
        label={i18n.t('campaignInfo.labels.dailyBudgetPlan')}
        name='dailyBudgetPlan'
      >
        {isScheduleDistribution ? (
          <LabelField
            value={i18n.t('campaignSummary.labels.budgetScheduleDistribution')}
            formGroupClassName={styles.marginBottom0}
          />
        ) : (
          <>
            <LabelField
              value={i18n.t('campaignInfo.labels.daily')}
              formGroupClassName={styles.marginBottom7}
            />
            <FormikField.InputGroup
              prefix={this.currencyLabel}
              name='dailyTargetBudget'
              hint={this.getDailyBudgetTips()}
              type='number'
              min={0}
              autoComplete='off'
              formGroupClassName={styles.marginBottom0}
            />
          </>
        )}
      </CustomField>
    );
  }

  renderCampaignScheduling() {
    const { startDate, endDate } = this.props;
    const endDateMoment = moment(endDate);
    const startDateMoment = moment(startDate);
    const days = endDateMoment.diff(startDateMoment, 'days') + 1;
    const hours = endDateMoment.diff(startDateMoment, 'hours') + 1;
    if (days > 1) {
      return (
        <Trans
          i18nKey='campaign.descriptions.campaignSchedulingDay'
          values={{ days }}
          components={{ 1: <span className='text-dark' /> }}
        />
      );
    } else if (days === 1) {
      return (
        <Trans
          i18nKey='campaign.descriptions.campaignSchedulingHour'
          values={{ hours }}
          components={{ 1: <span className='text-dark' /> }}
        />
      );
    }
  }

  render() {
    const {
      autoOptimise,
      editL1ObjectPath,
      errors,
      orderName,
      minDate,
      maxDate,
      canEditPriceModel,
      priceModelOptions,
      agencyProfit,
      priceModel,
      canUseDailyBudget,
      canEditBudgetPlan,
      onChangeDailyBudgetPlan: handleChangeDailyBudgetPlan,
      onChangePriceModel: handleChangePriceModel,
      onChangeSchedule: handleChangeSchedule,
      showTagsPlaceholder,
      isCampaignStart,
      videoAdViewObjectiveOptions,
      showOrderPriceField,
      dailyBudgetType,
      onlySupportAcceleratedDeliverType,
      // props 沒有型別（any），漏傳就會靜默把欄位藏掉，所以預設顯示
      showDeliverType = true,
      deliverType
    } = this.props;

    const priceModelClassName = cx('priceModel', {
      componentWithTips: true
    });
    const dateRangePickerClassName = cx('componentWithTips', {
      disableStart: isCampaignStart
    });
    const CBOBudgetFormatter = () => {
      return i18n.t('campaignInfo.labels.CBOBudgetHint');
    };
    const onChangeSchedule = (startDate, endDate) => {
      handleChangeSchedule(startDate, endDate);
      this.setNeedShowDailyBudgetAlert(true);
    };
    const priceModelFormatter = (priceModel) =>
      i18n.t(`campaign.labels.${priceModel}`);
    return (
      <fieldset>
        <legend>
          <span>{i18n.t('campaignInfo.labels.title')}</span>
        </legend>
        <div className={styles.component}>
          {this.state.alertContent && (
            <AlertDialog
              title={i18n.t('common.warning')}
              message={this.state.alertContent}
              dismiss={this.dismissAlertWindow}
            />
          )}
          <LabelField
            label={i18n.t('campaignInfo.labels.order')}
            name='orderName'
            value={orderName}
          />
          <FormikField.Input
            label={i18n.t('campaignInfo.labels.campaignName')}
            name='name'
          />
          <FormikField.DateRangePicker
            label={i18n.t('campaignInfo.labels.dayRange')}
            className={dateRangePickerClassName}
            minDate={minDate}
            maxDate={maxDate}
            startDateFormikName='startDate'
            endDateFormikName='endDate'
            name='dayRange'
            timeInterval={60}
            startDatePickerDisabled={isCampaignStart}
            onPopOverClosed={this.checkIsUnderBudget}
            onChange={onChangeSchedule}
            hoverHint={i18n.t('campaignInfo.labels.dateCannotModifyHint')}
            hint={this.renderCampaignScheduling()}
          />
          {canEditPriceModel ? (
            <FormikField.Select
              className={priceModelClassName}
              name='priceModel'
              label={i18n.t('campaignInfo.labels.priceModel')}
              simpleValue
              options={priceModelOptions}
              onChange={handleChangePriceModel}
              hoverHint={i18n.t(
                'campaignInfo.labels.cannotModifyPriceModelHint'
              )}
            />
          ) : (
            <FormikField.Label
              name='priceModel'
              label={i18n.t('campaignInfo.labels.priceModel')}
              formatter={priceModelFormatter}
            />
          )}
          {videoAdViewObjectiveOptions && this.renderViewObjective()}
          {showOrderPriceField &&
            this.renderOrderPrice(priceModel, canEditPriceModel)}
          {autoOptimise ? (
            <FormikField.Label
              label={i18n.t('campaignInfo.labels.totalBudget')}
              name='budget'
              formatter={CBOBudgetFormatter}
              hint={
                errors.budget ? (
                  <div className={styles.budgetError}>
                    <div className='errorTip'>{errors.budget}</div>
                    <Link to={editL1ObjectPath}>
                      {i18n.t('campaignInfo.labels.backToEditRtbCampaignGroup')}
                    </Link>
                  </div>
                ) : (
                  ''
                )
              }
            />
          ) : (
            this.renderTargetBudgetInput()
          )}
          {canUseDailyBudget &&
            !canEditBudgetPlan &&
            this.renderDailyBudgetPlan()}
          {canUseDailyBudget && canEditBudgetPlan && (
            <RadioField
              name='dailyBudgetType'
              label={i18n.t('campaignInfo.labels.dailyBudgetPlan')}
              className={styles.radio}
              useOptionExtraAsHoverTip={true}
              options={this.dudgetTypeOptions}
              value={dailyBudgetType}
              onChange={handleChangeDailyBudgetPlan}
            />
          )}
          {canUseDailyBudget &&
            +dailyBudgetType === DailyBudgetPlan.DAILY &&
            canEditBudgetPlan && (
              <FormikField.InputGroup
                label=''
                formGroupClassName={styles.dailyBudgetInput}
                name='dailyTargetBudget'
                prefix={this.currencyLabel}
                hint={this.getDailyBudgetTips()}
                type='number'
                min={0}
                autoComplete='off'
                onBlur={this.checkIsUnderBudget}
                onChange={this.turnOnNeedShowDailyBudgetAlert}
              />
            )}
          {showDeliverType && (
            <FormikField.ToggleButton
              name='deliverType'
              fieldContentWidth={'auto'}
              label={i18n.t('campaign.labels.deliverType')}
              options={this.props.campaignDeliverTypeOptions}
              disabledValues={
                onlySupportAcceleratedDeliverType ? [DeliverType.STANDARD] : []
              }
              postComponent={
                +deliverType === DeliverType.ACCELERATED ? (
                  <Tips>
                    {i18n.t('campaignInfo.hints.acceleratedDeliverType')}
                  </Tips>
                ) : undefined
              }
            />
          )}
          <FormikField.Tags
            label={i18n.t('campaign.labels.tags')}
            name='tags'
            placeholder={
              showTagsPlaceholder ? i18n.t('campaign.placeholders.tags') : ''
            }
          />
          <PermissionChecker
            permissionAware={notRoles(
              RoleNames.adsAdmin,
              RoleNames.adsReport,
              RoleNames.adsSales
            ).and(
              addOnEnabled(ADDONFEATURE.CAMPAIGN.ORDER_AGENCY_PROFIT_SETTING)
            )}
          >
            <LabelField
              label={i18n.t('campaign.labels.agencyProfit')}
              name='agencyProfit'
              value={agencyProfit}
            />
          </PermissionChecker>
        </div>
      </fieldset>
    );
  }
}

export default RtbCampaignInfo;
